import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../design-system/Icon";
import { useIpc } from "../ipc/IpcProvider";
import type { AccountsSnapshot } from "../ipc/types";

/**
 * Selector de cuenta de Mercado Libre (multi-cuenta, Fase MC1). Muestra la cuenta activa y permite
 * cambiar entre perfiles o agregar uno nuevo. Al cambiar/agregar recarga la app para que todas las
 * pantallas reflejen la cuenta activa (sus credenciales). Conectar el perfil nuevo se hace en Seguridad.
 */
export function AccountSwitcher(): JSX.Element | null {
  const client = useIpc();
  const navigate = useNavigate();
  const [snap, setSnap] = useState<AccountsSnapshot | null>(null);
  const [open, setOpen] = useState(false);
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editNick, setEditNick] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { client.getAccounts().then((r) => { if (r.ok) setSnap(r.value); }); }, [client]);
  useEffect(() => {
    const onDoc = (e: MouseEvent): void => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!snap) return null;
  const active = snap.items.find((a) => a.id === snap.activeId) ?? snap.items[0];

  const switchTo = async (id: string): Promise<void> => {
    if (id === snap.activeId) { setOpen(false); return; }
    setBusy(true);
    const r = await client.setActiveAccount(id);
    if (r.ok) window.location.reload(); else setBusy(false);
  };
  const add = async (): Promise<void> => {
    const name = nick.trim();
    if (!name) return;
    setBusy(true);
    const r = await client.addAccount(name);
    if (r.ok) window.location.reload(); else setBusy(false);
  };
  const saveRename = async (id: string): Promise<void> => {
    const name = editNick.trim();
    setEditId(null);
    if (!name) return;
    const r = await client.renameAccount(id, name);
    if (r.ok) setSnap(r.value);
  };
  const remove = async (id: string): Promise<void> => {
    if (!snap) return;
    const wasActive = id === snap.activeId;
    const r = await client.removeAccount(id);
    if (r.ok) { if (wasActive) window.location.reload(); else setSnap(r.value); }
  };
  const goConsolidated = (): void => { setOpen(false); navigate("/cuentas"); };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="iconbtn" style={{ width: "auto", padding: "0 10px", gap: 8, display: "inline-flex", alignItems: "center" }}
        onClick={() => setOpen((v) => !v)} aria-label="Cuenta de Mercado Libre" title="Cuenta de Mercado Libre">
        <Icon name="marketplace" size={16} />
        <span style={{ fontSize: 13, fontWeight: 600, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{active?.nickname ?? "Cuenta"}</span>
        <Icon name={open ? "arrow-up" : "arrow-down"} size={12} />
      </button>
      {open ? (
        <div className="card" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 260, zIndex: 50, padding: 8 }}>
          <div className="faint" style={{ fontSize: 11.5, padding: "2px 8px 6px" }}>CUENTAS DE MERCADO LIBRE</div>
          <div style={{ display: "grid", gap: 2 }}>
            {snap.items.map((a) => (
              <div key={a.id} className="row" style={{ gap: 4, padding: "4px 6px", borderRadius: 6, background: a.id === snap.activeId ? "color-mix(in srgb, var(--brand) 12%, transparent)" : "none" }}>
                <span title={a.connected ? "Conectada" : "Sin conectar"} style={{ width: 8, height: 8, borderRadius: "50%", background: a.connected ? "var(--success)" : "var(--text-faint)", flexShrink: 0 }} />
                {editId === a.id ? (
                  <input className="pd-input" style={{ height: 28, fontSize: 12.5, flex: 1 }} autoFocus value={editNick}
                    onChange={(e) => setEditNick(e.target.value)} onBlur={() => void saveRename(a.id)}
                    onKeyDown={(e) => { if (e.key === "Enter") void saveRename(a.id); if (e.key === "Escape") setEditId(null); }} aria-label="Nuevo nombre" />
                ) : (
                  <button type="button" disabled={busy} onClick={() => switchTo(a.id)}
                    style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "4px 2px", textAlign: "left", color: "inherit", fontSize: 13 }}>
                    <span className="strong" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.nickname}</span>
                    {a.id === snap.activeId ? <Icon name="check" size={14} /> : null}
                  </button>
                )}
                <button type="button" aria-label={`Renombrar ${a.nickname}`} title="Renombrar" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-faint)", padding: 2, fontSize: 12 }} onClick={() => { setEditId(a.id); setEditNick(a.nickname); }}>✎</button>
                {snap.items.length > 1 ? <button type="button" aria-label={`Eliminar ${a.nickname}`} title="Eliminar" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--danger)", padding: 2, fontSize: 12 }} onClick={() => remove(a.id)}>🗑</button> : null}
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 8, display: "flex", gap: 6 }}>
            <input className="pd-input" style={{ height: 32, fontSize: 12.5, flex: 1 }} placeholder="Nombre (ej. Empresa)" value={nick}
              onChange={(e) => setNick(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void add(); }} aria-label="Nombre de la nueva cuenta" />
            <button type="button" className="btn btn--primary btn--sm" disabled={busy || nick.trim().length === 0} onClick={add}>Agregar</button>
          </div>
          <button type="button" onClick={goConsolidated} style={{ width: "100%", marginTop: 6, background: "none", border: "none", cursor: "pointer", color: "var(--brand)", fontSize: 12.5, padding: "6px 8px", textAlign: "left" }}>📊 Ver todas las cuentas (consolidado)</button>
          <div className="faint" style={{ fontSize: 11, padding: "2px 8px" }}>Tras agregar, conéctala en Seguridad con su OAuth.</div>
        </div>
      ) : null}
    </div>
  );
}
